import { EmptyState } from '../../../components/empty-state';
import styles from './campaign.module.css';
import { ChapterDetailView } from './chapter-view';
import { ActivityDetailView, MaterialDetailView } from './activity-view';
import { Hero } from './hero';
import { ProgressBar } from '../../../components/progress-bar';
import { ProgressPill } from '../progress-pill';
import { Pill } from '../../../components/pill';
import { Card } from '../../../components/card';
// Student campaign detail — the campaign overview, and the entry point to the
// views below it.
//
// The nesting is local state rather than routes, and each level now has its own
// file: chapter-view, activity-view, gameplay-view, plus the cards the three of
// them share in activity-cards. They used to be seventeen components in one
// 1,495-line file, which is also why four stylesheets pointed at it.

import { Button } from '../../../components/button';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  useStudentCampaign,

} from '../../../api/student/campaigns/hooks';
import type {
  StudentCampaignDetail as Detail,
} from '../../../api/student/campaigns/types';
import type { StudentLocation, StudentNavigate } from '../student-location';
import {
  campaignStatus,
  chapterAvailability,
  chapterStatus,
  chapterMandatoryComplete,
  chapterProgress,
  campaignXp,
  sessionsOver,
  type ProgressStatus,
} from '../../../api/student/campaigns/gating';
import { useRefetchOnForeground } from '../../../hooks/use-refetch-on-foreground';
import { CampaignCertificatePanel } from '../certificate/campaign-certificate';
import tabs from '../filter-row.module.css';
import { CampaignStats } from './campaign-stats';
import { ChapterProgressWords } from './chapter-progress';
import { shutWords } from './chapter-shut';

interface Props {
  id: string;
  location: StudentLocation;
  navigate: StudentNavigate;
  back: () => void;
}

/** Renders the skill rewards an activity grants on completion. */
type DetailTab = 'overview' | 'chapters' | 'certificate' | 'about';

/** Each tab's name: its key, written whole. */
const TAB_KEY: Record<DetailTab, string> = {
  overview: 'student.campaign.tabs.overview',
  chapters: 'student.campaign.tabs.chapters',
  certificate: 'student.campaign.tabs.certificate',
  about: 'student.campaign.tabs.about',
};

/**
 * Chapter-trail node state → module class.
 *
 * An explicit map rather than `styles[`cnode${status}`]`: composing the key at
 * runtime is what let `.cnode--done` sit in the stylesheet while chapterStatus
 * emitted `completed`, so a finished chapter had no treatment and nothing could
 * see the mismatch.
 */
const CNODE_STATE: Partial<Record<ProgressStatus, string>> = {
  completed: styles.cnodeCompleted,
  active: styles.cnodeActive,
  locked: styles.cnodeLocked,
};

export function StudentCampaignDetail({ id, location, navigate, back }: Props) {
  const { t } = useTranslation();
  // Server state comes from the domain query; completing an activity / submitting
  // a reflection invalidates this query (via the mutation hooks), so the detail
  // refetches automatically — no manual reload() needed.
  const campaignQuery = useStudentCampaign(id);
  const { data, isLoading, error } = campaignQuery;
  // Re-check the real state when the student returns to the portal tab (e.g.
  // after closing the game tab when a match ends).
  useRefetchOnForeground(campaignQuery.refetch);
  // The open chapter/activity/material come from the history-backed location.
  const openChapterId = location.kind === 'chapter' ? location.chapterId : null;
  const openActivity =
    location.kind === 'activity' ? { chapterId: location.chapterId, activityId: location.activityId } : null;
  const openMaterial =
    location.kind === 'material' ? { chapterId: location.chapterId, materialId: location.materialId } : null;
  const [tab, setTab] = useState<DetailTab>(
    () => (location.kind === 'campaign' ? location.tab : undefined) ?? 'overview',
  );

  if (isLoading) return <EmptyState>{t('common.loading')}</EmptyState>;
  if (error || !data) {
    return (
      <div className="tone-danger">
        {error instanceof Error ? error.message : t('requestErrors.notFound')}
      </div>
    );
  }

  const { xpEarned: earnedXp, xpTotal: totalXp, xpPct: pct } = campaignXp(data);

  // Certificate is earned at ≥80% of activities completed (matches the backend).
  const certTotalActs = data.chapters.reduce((s, ch) => s + ch.activities.length, 0);
  const certDoneActs = data.chapters.reduce(
    (s, ch) => s + ch.activities.filter((a) => a.completion != null).length,
    0,
  );
  const certEarned = certTotalActs > 0 && certDoneActs / certTotalActs >= 0.8;

  const campaignBucket = campaignStatus(data);

  // ── Material sub-view ────────────────────────
  if (openMaterial) {
    const chapter = data.chapters.find((c) => c.id === openMaterial.chapterId);
    const material = chapter?.materials?.find((m) => m.id === openMaterial.materialId);
    if (chapter && material) {
      return (
        <MaterialDetailView
          campaignId={data.id}
          campaignName={data.name}
          chapter={chapter}
          material={material}
          onBackToCampaign={() => navigate({ kind: 'campaign', campaignId: id })}
          onBackToChapter={back}
        />
      );
    }
  }

  // ── Activity sub-view ─────────────────────────
  if (openActivity) {
    const chapter = data.chapters.find((c) => c.id === openActivity.chapterId);
    const activity = chapter?.activities.find((a) => a.id === openActivity.activityId);
    if (chapter && activity) {
      return (
        <ActivityDetailView
          campaign={data}
          chapter={chapter}
          activity={activity}
          onBackToCampaign={() => navigate({ kind: 'campaign', campaignId: id })}
          onBackToChapter={() => navigate({ kind: 'chapter', campaignId: id, chapterId: chapter.id })}
          onCompleted={() => navigate({ kind: 'chapter', campaignId: id, chapterId: chapter.id })}
        />
      );
    }
  }

  // ── Chapter sub-view (activities grid) ────────
  if (openChapterId) {
    const chapter = data.chapters.find((c) => c.id === openChapterId);
    if (chapter) {
      return (
        <ChapterDetailView
          campaign={data}
          chapter={chapter}
          onBackToCampaign={() => navigate({ kind: 'campaign', campaignId: id })}
          onBackToList={() => navigate({ kind: 'campaigns' })}
          onOpenActivity={(activityId) => navigate({ kind: 'activity', campaignId: id, chapterId: chapter.id, activityId })}
          onOpenMaterial={(materialId) => navigate({ kind: 'material', campaignId: id, chapterId: chapter.id, materialId })}
        />
      );
    }
  }

  // ── Campaign overview ─────────────────────────
  return (
    <div className="page">
      <nav className="crumb">
        <button type="button" onClick={() => navigate({ kind: 'campaigns' })}>{t('student.campaigns.title')}</button>
        <span className="sep">›</span>
        <span>{data.name}</span>
      </nav>

      <Hero size="campaign" image={data.imageUrl} badge={<ProgressPill of="campaign" status={campaignBucket} />} title={data.name}>
        <p className={`${styles.cdHero__role}`}>{data.gameName} · {data.worldName}</p>
        <div className={`${styles.cdHero__stats}`}>
          <CampaignStats earnedXp={earnedXp} totalXp={totalXp} kpBalance={data.kpBalance} />
          <div className={`${styles.cdHero__bar}`}>
            <ProgressBar value={pct} grow />
            <span className={styles.crow__pct}>{pct}%</span>
          </div>
          <button
            type="button"
            className={`${styles.cdCert} ${certEarned ? styles.cdCertEarned : styles.cdCertLocked}`}
            onClick={() => setTab('certificate')}
            title={t('student.campaign.certificate.title')}
          >
            <span className={`${styles.cdCert__icon}`}>{certEarned ? '🏅' : '🔒'}</span>
            {t(certEarned ? 'student.campaign.certificate.headerEarned' : 'student.campaign.certificate.headerLocked')}
          </button>
        </div>
      </Hero>

      <div className={`${styles.cdLayout}`}>
        <div className={`${styles.cdMain}`}>
          <div className={tabs.filters}>
            {(['overview', 'chapters', 'certificate', 'about'] as DetailTab[]).map((tb) => (
              <button
                key={tb}
                type="button"
                className={`${tabs.filter} ${tab === tb ? tabs.filterActive : ''}`}
                onClick={() => setTab(tb)}
              >
                {t(TAB_KEY[tb])}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <div>
              <Card framed padded={false} className={`${styles.cdOverview}`}>
                {/* Past its end, it says the live sessions are over and invites the student to carry on, not to start. */}
                <p>{t(campaignBucket === 'ended' ? 'student.campaign.overviewBodyEnded' : 'student.campaign.overviewBody', { name: data.name })}</p>
              </Card>
              <CurrentChapterCallout
                campaign={data}
                onOpenChapter={(chapterId) => navigate({ kind: 'chapter', campaignId: id, chapterId })}
                onOpenChapters={() => setTab('chapters')}
              />
            </div>
          )}

          {tab === 'chapters' && (
            <section className={`${styles.ctrail}`}>
              {data.chapters.map((ch, idx) => {
                const status = chapterStatus(ch, data);
                const av = chapterAvailability(ch, data);
                const locked = !av.chapterOpen && status !== 'completed';
                const marker = status === 'completed' ? '✓' : locked ? '🔒' : idx + 1;
                return (
                  <div key={ch.id} className={`${styles.cnode} ${CNODE_STATE[status] ?? ''}`}>
                    <div className={`${styles.cnode__rail}`}><span className={`${styles.cnode__marker}`}>{marker}</span></div>
                    <Card padded={false} className={`${styles.cnode__card}`} onClick={() => navigate({ kind: 'chapter', campaignId: id, chapterId: ch.id })}>
                      <div className={`${styles.cnode__body}`}>
                        <h3 className={`${styles.chap__title}`}>{ch.title}</h3>
                        {ch.description && <p className={`${styles.chap__desc}`}>{ch.description}</p>}
                        {/* Every reason it is shut, outermost first, in the words a shut activity's note uses for its first. */}
                        {av.shut.map((why) => (
                          <p key={why} className={`${styles.chap__hint}`}>🔒 {shutWords(t, why, ch)}</p>
                        ))}
                        <div className={`${styles.chap__prog}`}>
                          <ProgressBar value={chapterProgress(ch).xpPct} grow gold={status === 'completed'} />
                          <ChapterProgressWords chapter={ch} />
                        </div>
                      </div>
                      <ProgressPill of="chapter" status={status} />
                    </Card>
                  </div>
                );
              })}
            </section>
          )}

          {tab === 'certificate' && <CampaignCertificatePanel campaignId={id} />}

          {tab === 'about' && (
            <Card framed padded={false} className={`${styles.cdPlaceholder}`}><p>{t('student.campaign.aboutComingSoon')}</p></Card>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Chapter sub-view ────────────────────────────

// ── Current / Next chapter callout (Overview tab) ──────────────

interface CurrentChapterCalloutProps {
  campaign: Detail;
  onOpenChapter: (chapterId: string) => void;
  onOpenChapters: () => void;
}

/**
 * Surfaces the most relevant chapter in the Campaign Overview:
 *   - If any chapter is currently active (within its start/end window), show
 *     it as "Current chapter" with a Continue CTA.
 *   - Otherwise, show the next upcoming chapter (earliest startDateUtc in the
 *     future) as "Next chapter".
 *   - Otherwise (everything done or no schedule), shows an all-done state.
 *
 * "Continue / Start" jumps straight into the chapter detail sub-view, the
 * same flow the Chapters tab uses.
 */
function CurrentChapterCallout({ campaign, onOpenChapter, onOpenChapters }: CurrentChapterCalloutProps) {
  const { t } = useTranslation();

  const ordered = [...campaign.chapters].sort((a, b) => a.orderIndex - b.orderIndex);
  // The current chapter advances once its MANDATORY activities are done — left-
  // over optional activities don't keep an earlier chapter as "current".
  const currentChapter = ordered.find((ch) => {
    const av = chapterAvailability(ch, campaign);
    return av.chapterOpen && !chapterMandatoryComplete(ch, campaign);
  }) ?? null;
  const nextChapter = currentChapter
    ? null
    : ordered.find((ch) => chapterAvailability(ch, campaign).beforeStart) ?? null;
  // A chapter with nothing left that can still be done (ended) is no place to send the student.
  const fallback = currentChapter || nextChapter || ordered.find((ch) => !['completed', 'ended'].includes(chapterStatus(ch, campaign)));
  const shown = currentChapter ?? nextChapter ?? fallback ?? null;
  const over = sessionsOver(campaign);

  if (!shown) {
    return (
      <Card framed padded={false} className={`${styles.ccCallout}`}>
        <h3 className={`${styles.ccCallout__title}`}>{t(over ? 'student.campaign.nothingLeft' : 'student.campaign.allChaptersComplete')}</h3>
      </Card>
    );
  }

  const isCurrent = shown === currentChapter;
  const labelKey = isCurrent
    ? 'student.campaign.currentChapter'
    : nextChapter
      ? 'student.campaign.nextChapter'
      : 'student.campaign.chapter';
  // Past the campaign's end the student is carrying on, never starting.
  const ctaKey = isCurrent || over ? 'student.campaign.continueChapter' : 'student.campaign.startChapter';

  const chapterNumber = ordered.findIndex((c) => c.id === shown.id) + 1;
  const ctaEnabled = chapterAvailability(shown, campaign).chapterOpen;

  return (
    <Card framed padded={false} className={`${styles.ccCallout}`}>
      <div className={`${styles.ccCallout__header}`}>
        <Pill tone="active">{t(labelKey)}</Pill>
        <Button variant="link" onClick={onOpenChapters}>
          {t('student.campaign.viewAllChapters')} →
        </Button>
      </div>
      <h3 className={`${styles.ccCallout__title}`}>{t('student.campaign.chapterNumber', { number: chapterNumber })}: {shown.title}</h3>
      {shown.description && (
        <p className={`${styles.ccCallout__desc}`}>{shown.description}</p>
      )}
      <div className={`${styles.ccCallout__prog}`}>
        <ProgressBar value={chapterProgress(shown).xpPct} grow />
        <span className={`${styles.ccCallout__progLabel}`}>
          <ChapterProgressWords chapter={shown} />
        </span>
      </div>
      <Button onClick={() => onOpenChapter(shown.id)} disabled={!ctaEnabled}>
        {ctaEnabled ? t(ctaKey) : t('student.campaign.chapterLockedShort')}
      </Button>
    </Card>
  );
}

